// src/quiz/gre-mock7/quantitativeData.js
// GRE Mock 7 — Quantitative Reasoning.
// Section 1: 12 questions (21 min). Section 2: 15 questions (26 min).
// Question format: see ../greQuestionTypes.js
import { QC_OPTIONS } from "../greQuestionTypes";

const QC_QUESTION = "Compare Quantity A and Quantity B, using additional information centered above the two quantities if such information is given.";
const [QC_A, QC_B, QC_EQUAL, QC_UNKNOWN] = QC_OPTIONS;

// ================= SECTION 1 =================

export const section1Questions = [
  {
    id: 1,
    question: QC_QUESTION,
    quantityA: "The greatest prime number less than 20",
    quantityB: "The least prime number greater than 17",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "19 is prime, so it is the greatest prime below 20. Above 17, 18 is even and 19 is prime. Both quantities are 19.",
  },
  {
    id: 2,
    question: QC_QUESTION,
    quantityA: "35% of 280",
    quantityB: "100",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "35% of 280 = 0.35 × 280 = 98, which is less than 100.",
  },
  {
    id: 3,
    question: "A printer prints 45 pages per minute. How many minutes does it need to print 540 pages?",
    options: ["10", "12", "15", "18"],
    answer: "12",
    explanation: "Time = pages ÷ rate = 540 ÷ 45 = 12 minutes.",
  },
  {
    id: 4,
    question: "If x = 4y + 3 and y = 5, what is the value of x?",
    options: ["20", "23", "25", "28"],
    answer: "23",
    explanation: "Substitute y = 5: x = 4(5) + 3 = 20 + 3 = 23.",
  },
  {
    id: 5,
    question: "Which of the following integers is a multiple of both 4 and 6?",
    options: ["8", "18", "24", "30"],
    answer: "24",
    explanation: "A multiple of both 4 and 6 is a multiple of their LCM, 12. Only 24 qualifies; 8 is not divisible by 6, and 18 and 30 are not divisible by 4.",
  },
  {
    id: 6,
    question: "A notebook costs $2.40 and a pencil costs $0.35. What is the total cost of 5 notebooks and 12 pencils?",
    options: ["$15.20", "$16.20", "$16.80", "$17.20"],
    answer: "$16.20",
    explanation: "5 × $2.40 = $12.00 and 12 × $0.35 = $4.20. Total = $16.20.",
  },
  {
    id: 7,
    question: "Pipe A can fill a tank in 15 hours and pipe B can fill it in 10 hours. Working together, how many hours do they need to fill the tank?",
    options: ["5", "6", "8", "12"],
    answer: "6",
    explanation: "Per hour, A fills 1/15 and B fills 1/10 of the tank, together 2/30 + 3/30 = 1/6. So the full tank takes 6 hours.",
  },
  {
    id: 8,
    question: "An employee earns a salary of $4,000. The salary is raised by 10% and later reduced by 10%. What is the final salary?",
    options: ["$3,900", "$3,960", "$4,000", "$4,040"],
    answer: "$3,960",
    explanation: "$4,000 × 1.1 = $4,400, then $4,400 × 0.9 = $3,960. The 10% cut comes off a larger amount, so the changes don't cancel.",
  },
  {
    id: 9,
    question: "The ratio of A to B is 3 : 5 and the ratio of B to C is 10 : 7. What is the ratio of A to C?",
    options: ["3 : 7", "6 : 7", "5 : 7", "3 : 2"],
    answer: "6 : 7",
    explanation: "Make B the same in both ratios: A : B = 6 : 10 and B : C = 10 : 7. So A : C = 6 : 7.",
  },
  {
    id: 10,
    question: "Which of the following is equivalent to 5(2x + 1) − 3(x − 4)?",
    options: ["7x + 17", "7x − 7", "13x + 17", "7x + 9"],
    answer: "7x + 17",
    explanation: "Distribute: 10x + 5 − 3x + 12 = 7x + 17. Watch the sign: −3 × −4 = +12.",
  },
  {
    id: 11,
    question: "What is the value of (2x + 5)/(x − 1) when x = 3?",
    options: ["4", "5", "5.5", "6.5"],
    answer: "5.5",
    explanation: "(2·3 + 5)/(3 − 1) = 11/2 = 5.5.",
  },
  {
    id: 12,
    question: QC_QUESTION,
    given: "x is a positive integer.",
    quantityA: "x",
    quantityB: "x²",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "If x = 1 the quantities are equal (1 = 1); if x = 2, x² = 4 is greater. Since both cases are possible, the relationship cannot be determined.",
  },
];

// ================= SECTION 2 =================

export const section2Questions = [
  {
    id: 13,
    question: "What is the median of the data set 14, 4, 11, 9, 12?",
    options: ["9", "10", "11", "12"],
    answer: "11",
    explanation: "Put the values in order: 4, 9, 11, 12, 14. The middle (third) value is 11.",
  },
  {
    id: 14,
    question: "The average of five numbers is 12. If one of the numbers is 7, what is the average of the remaining four numbers?",
    options: ["12.5", "13", "13.25", "14"],
    answer: "13.25",
    explanation: "The five numbers sum to 5 × 12 = 60. Removing 7 leaves 53, and 53 ÷ 4 = 13.25.",
  },
  {
    id: 15,
    question: "A survey found that 45% of 360 participants preferred online classes. How many participants preferred online classes?",
    options: ["144", "162", "180", "198"],
    answer: "162",
    explanation: "45% of 360 = 0.45 × 360 = 162.",
  },
  {
    id: 16,
    question: "What are the solutions of x² − 8x + 15 = 0?",
    options: ["2 and 6", "3 and 5", "−3 and −5", "1 and 15"],
    answer: "3 and 5",
    explanation: "Factor: (x − 3)(x − 5) = 0, so x = 3 or x = 5. (−3 and −5 would solve x² + 8x + 15 = 0.)",
  },
  {
    id: 17,
    question: "What is the least common multiple of 12 and 18?",
    options: ["24", "36", "54", "72"],
    answer: "36",
    explanation: "12 = 2² × 3 and 18 = 2 × 3². Take the highest power of each prime: 2² × 3² = 36.",
  },
  {
    id: 18,
    question: "If a/b = 7/9 and b = 27, what is the value of a?",
    options: ["18", "21", "24", "27"],
    answer: "21",
    explanation: "Multiply both sides by b: a = (7/9) × 27 = 21.",
  },
  {
    id: 19,
    question: "The price of an item is decreased by 20% and then increased by 20%. What is the net change in price?",
    options: ["No change", "4% decrease", "4% increase", "10% decrease"],
    answer: "4% decrease",
    explanation: "0.8 × 1.2 = 0.96, so the final price is 96% of the original: a 4% decrease.",
  },
  {
    id: 20,
    question: "In a right triangle, one of the non-right angles measures 35°. What is the measure of the other non-right angle?",
    options: ["45°", "55°", "65°", "75°"],
    answer: "55°",
    explanation: "The two acute angles of a right triangle sum to 90°, so the other angle is 90° − 35° = 55°.",
  },
  {
    id: 21,
    question: QC_QUESTION,
    quantityA: "5/8",
    quantityB: "7/11",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "Cross-multiply: 5 × 11 = 55 and 7 × 8 = 56. Since 55 < 56, 5/8 < 7/11.",
  },
  {
    id: 22,
    question: "A rectangle has a perimeter of 40 and its length is three times its width. What is its area?",
    options: ["60", "75", "80", "100"],
    answer: "75",
    explanation: "With width w and length 3w, the perimeter is 8w = 40, so w = 5 and the length is 15. Area = 5 × 15 = 75.",
  },
  {
    id: 23,
    question: "A sum of $5,000 is invested at 4% simple interest per year. What is the total amount after 3 years?",
    options: ["$5,400", "$5,600", "$5,624", "$6,000"],
    answer: "$5,600",
    explanation: "Simple interest = $5,000 × 0.04 × 3 = $600, so the total is $5,600. ($5,624 is the compounded amount.)",
  },
  {
    id: 24,
    question: "In how many ways can a team of 3 people be chosen from a group of 6 people?",
    options: ["15", "18", "20", "24"],
    answer: "20",
    explanation: "Order doesn't matter in a team: C(6, 3) = (6 × 5 × 4)/(3 × 2 × 1) = 20.",
  },
  {
    id: 25,
    question: "If 3^(x − 1) = 81, what is the value of x?",
    options: ["4", "5", "6", "7"],
    answer: "5",
    explanation: "81 = 3⁴, so x − 1 = 4 and x = 5.",
  },
  {
    id: 26,
    question: QC_QUESTION,
    given: "x + y = 8 and xy = 15.",
    quantityA: "x² + y²",
    quantityB: "30",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "x² + y² = (x + y)² − 2xy = 64 − 30 = 34, which is greater than 30. (The numbers are 3 and 5: 9 + 25 = 34.)",
  },
  {
    id: 27,
    question: "A bus travels half of a trip's distance at 20 km/h and the other half at 30 km/h. What is its average speed for the whole trip?",
    options: ["24 km/h", "25 km/h", "26 km/h", "28 km/h"],
    answer: "24 km/h",
    explanation: "Take a 120 km trip: 60 km at 20 km/h takes 3 hours, 60 km at 30 km/h takes 2 hours. 120 km ÷ 5 hours = 24 km/h—below 25 because more time is spent at the slower speed.",
  },
];
